import { useLazyQuery, useQuery } from '@apollo/client'
import { GetCandidatesForRosterSqlQuery } from '__generated__/graphql'
import { createContext, ReactNode, useEffect, useState } from 'react'
import {
    GetCandidatesForRosterSql,
    GetCandidateSearchSqlAllIds,
} from 'recruiter/contexts/CandidateSearchContext'
import useToast from 'shared/components/Toast/useToast'
import {
    CandidateFilterState,
    SortState,
    useCandidateSearchUrlState,
} from 'shared/hooks/candidateSearchUrlState'
import { useReferenceSearchesUrlState } from 'shared/hooks/useReferenceSearchesUrlState'
import { CandidateSearchSortInput } from '__generated__/graphql'

export type RosterCandidateTableRowModel =
    GetCandidatesForRosterSqlQuery['candidateSearchSql']['data'][number]

export interface CandidateTableState {
    page: number
    limit: number
    filters: CandidateFilterState
    sort: SortState
    graphQLSort: CandidateSearchSortInput[] | undefined
}

export interface CandidateSqlSearchContextValue {
    tableState: CandidateTableState
    setTableState: (partial: Partial<CandidateTableState>) => void
    clearFilters: () => void
    hasActiveFilters: boolean
    rosterId: string | undefined
    enrichmentRosterId: string | undefined
    data: RosterCandidateTableRowModel[]
    isLoading: boolean
    totalItems: number
    selectedCandidateIds: string[]
    setSelectedCandidateIds: (ids: string[]) => void
    selectAllCandidates: () => Promise<void>
    isSelectingAll: boolean
}

export const CandidateSqlSearchContext =
    createContext<CandidateSqlSearchContextValue | null>(null)

interface CandidateSqlSearchProviderProps {
    rosterId?: string
    enrichmentRosterId?: string
    children: ReactNode
}

export function CandidateSqlSearchProvider({
    rosterId,
    enrichmentRosterId,
    children,
}: CandidateSqlSearchProviderProps) {
    const toast = useToast()
    const urlState = useCandidateSearchUrlState()
    const { refColumnRosterIds } = useReferenceSearchesUrlState()
    const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>(
        []
    )

    const filtersKey = JSON.stringify(urlState.graphQLFilters)
    useEffect(() => {
        setSelectedCandidateIds([])
    }, [filtersKey])

    const tableState: CandidateTableState = {
        page: urlState.page,
        limit: urlState.limit,
        filters: urlState.filters,
        sort: urlState.sort,
        graphQLSort: urlState.graphQLSort,
    }

    const setTableState = (partial: Partial<CandidateTableState>) => {
        if (partial.page !== undefined) urlState.setPage(partial.page)
        if (partial.limit !== undefined) urlState.setLimit(partial.limit)
        if (partial.filters !== undefined) urlState.setFilters(partial.filters)
        if (partial.sort !== undefined) urlState.setSort(partial.sort)
    }

    const { data: queryData, loading } = useQuery(GetCandidatesForRosterSql, {
        variables: {
            rosterId,
            enrichmentRosterId,
            filters: urlState.graphQLFilters,
            page: tableState.page,
            limit: tableState.limit,
            sort: tableState.graphQLSort,
            referenceRosterIds:
                refColumnRosterIds.length > 0 ? refColumnRosterIds : undefined,
        },
        fetchPolicy: 'network-only',
        onError: (error) => {
            toast({
                title: 'Error',
                message: 'Unable to fetch candidates, please try again',
                severity: 'error',
            })
            console.error('Error fetching candidates:', error)
        },
    })

    const [fetchAllIds, { loading: isSelectingAll }] = useLazyQuery(
        GetCandidateSearchSqlAllIds,
        {
            fetchPolicy: 'network-only',
            onError: (error) => {
                toast({
                    title: 'Error',
                    message:
                        'Unable to select all candidates, please try again',
                    severity: 'error',
                })
                console.error('Error fetching all candidate IDs:', error)
            },
        }
    )

    const selectAllCandidates = async () => {
        const result = await fetchAllIds({
            variables: {
                rosterId,
                filters: urlState.graphQLFilters,
            },
        })
        if (result.data?.candidateSearchSqlAllIds) {
            setSelectedCandidateIds(result.data.candidateSearchSqlAllIds)
        }
    }

    const data = queryData?.candidateSearchSql?.data || []
    const totalItems = queryData?.candidateSearchSql?.pageInfo?.itemTotal ?? 0

    const value: CandidateSqlSearchContextValue = {
        tableState,
        setTableState,
        clearFilters: urlState.clearFilters,
        hasActiveFilters: urlState.hasActiveFilters,
        rosterId,
        enrichmentRosterId,
        data,
        isLoading: loading,
        totalItems,
        selectedCandidateIds,
        setSelectedCandidateIds,
        selectAllCandidates,
        isSelectingAll,
    }

    return (
        <CandidateSqlSearchContext.Provider value={value}>
            {children}
        </CandidateSqlSearchContext.Provider>
    )
}
